import type { KpiEnvelope, RegionGrain } from '~stores/kpi-envelope';

/**
 * Environments where a pre-production runtime warning is allowed to fire.
 *
 * An explicit allowlist rather than `!== 'production'`: `REACT_APP_ENV` is
 * injected per GitHub environment at build time, so an unset or unexpected
 * value (`''`, `'prod'`) must stay silent instead of warning in a user-facing
 * production bundle. `dev` is the value the CI `dev` environment and the local
 * `.env` convention use (see README); `development` is Vite's own default when
 * a dev server runs without one.
 */
export const PRE_PRODUCTION_WARN_ENVS = ['dev', 'development', 'staging'];

/**
 * Pre-production guard: the envelope's own `capabilities.regionGrain` is the
 * ground truth for how region actually resolved server-side. A mismatch against
 * the source's declared `regionGrain` means the CMS and API have drifted (e.g.
 * the backend widened or narrowed a KPI's region resolution without the source
 * catching up), which silently breaks the source's province-forwarding gate and
 * any grain-driven display. Warn loudly so it gets fixed instead of shipping
 * quietly wrong.
 *
 * Gated on `REACT_APP_ENV` (not `MODE`) so it still fires in staging, where
 * drift needs to be caught, and stays silent in production, where it would be
 * too late and user-facing noise. Never throws on a production render path.
 *
 * Shared by every envelope-backed source: the generic `createEnvelopeSource`
 * checks the one envelope it fetched, and the fan-out `app-downloads` source
 * checks each of its two.
 */
export const warnOnRegionGrainDrift = (
    label: string,
    endpoint: string,
    declared: RegionGrain,
    envelope: KpiEnvelope
): void => {
    if (
        !PRE_PRODUCTION_WARN_ENVS.includes(import.meta.env.REACT_APP_ENV) ||
        envelope.capabilities.regionGrain === declared
    ) {
        return;
    }
    console.warn(
        `KpiSource "${label}" (${endpoint}): declared regionGrain "${declared}" but the envelope reported "${envelope.capabilities.regionGrain}". CMS and API have drifted on this KPI's region resolution.`
    );
};
